import { ReactElement, useCallback, useMemo, useState } from 'react';

import { IdentityLogEvent } from '@localstack/types';
import { GridEventListener } from '@mui/x-data-grid';

import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { NavigateBefore as PrevIcon, NavigateNext as NextIcon, Refresh as RefreshIcon } from '@mui/icons-material';

import { EventLogTable } from './EventLogTable';
import { EventLogDrawer } from './EventLogDrawer';
import { FetchIdentityEventsFn, useIdentityLogPages } from './useIdentityLogPages';

const PaginationRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
}));

const PageLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

type IdentityLogContentProps = {
  fetchFn: FetchIdentityEventsFn;
  orgId?: string;
};

export const IdentityLogContent = ({ fetchFn, orgId }: IdentityLogContentProps): ReactElement => {
  const [selectedEvent, setSelectedEvent] = useState<Optional<IdentityLogEvent>>();

  const { currentPage, pageIndex, isLoading, hasPrevPage, hasNextPage, goToNextPage, goToPrevPage, refresh } =
    useIdentityLogPages(orgId, fetchFn);

  const rows = useMemo(
    () => (currentPage?.items ?? []).map((event) => ({ ...event, id: event.id ?? '' })),
    [currentPage],
  );

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>((params) => {
    const event = params.row as IdentityLogEvent;
    const hasDetails = !!event.idp_name || (!!event.extras && Object.keys(event.extras).length > 0);
    if (hasDetails) setSelectedEvent(event);
  }, []);

  const handleNextPage = () => {
    goToNextPage();
    setSelectedEvent(undefined);
  };

  const handlePrevPage = () => {
    goToPrevPage();
    setSelectedEvent(undefined);
  };

  const handleRefresh = () => {
    refresh();
    setSelectedEvent(undefined);
  };

  return (
    <Box mt={2}>
      <EventLogTable rows={rows} loading={isLoading} onRowClick={handleRowClick} />
      <EventLogDrawer event={selectedEvent} onClose={() => setSelectedEvent(undefined)} />

      <PaginationRow>
        <PageLabel variant="body2">Page {pageIndex + 1}</PageLabel>
        <IconButton size="small" onClick={handlePrevPage} disabled={!hasPrevPage}>
          <PrevIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleNextPage} disabled={!hasNextPage}>
          <NextIcon fontSize="small" />
        </IconButton>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={handleRefresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </PaginationRow>
    </Box>
  );
};
